// ClaudeCode: Client-side crypto — zero-knowledge encryption/decryption
// The server NEVER sees plaintext values, passphrases, or derived keys.
// All encryption/decryption happens in the browser.

import { argon2id } from '@noble/hashes/argon2';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';

// ── Key derivation (Argon2id) ──
// Parameters: t=3 iterations, m=65536 (64MB), p=4 parallelism
// Conservative defaults — can be tuned per the vault concept spec
const ARGON2_PARAMS = {
  t: 3, // iterations
  m: 65536, // 64MB memory
  p: 4, // parallelism
  dkLen: 32, // 256-bit key
};

// Derive a 256-bit AES key from passphrase + salt (from server)
export async function deriveKey(
  passphrase: string,
  saltHex: string
): Promise<CryptoKey> {
  const salt = hexToBytes(saltHex);
  const encoder = new TextEncoder();
  const passphraseBytes = encoder.encode(passphrase);

  // Argon2id key derivation (runs in main thread — consider Web Worker for UI responsiveness)
  const keyMaterial = argon2id(passphraseBytes, salt, ARGON2_PARAMS);

  // Import as AES-GCM key
  return crypto.subtle.importKey(
    'raw',
    keyMaterial.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false, // not extractable
    ['encrypt', 'decrypt']
  );
}

// ── Encrypt (for creating/updating entries) ──
// Returns base64(12-byte IV + ciphertext + 16-byte authTag)
export async function encrypt(
  plaintext: string,
  key: CryptoKey
): Promise<string> {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );

  // Pack: IV + ciphertext (includes authTag appended by WebCrypto)
  const packed = new Uint8Array(iv.length + ciphertext.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(ciphertext), iv.length);

  return btoa(String.fromCharCode(...packed));
}

// ── Decrypt (for reading entries) ──
// Input: base64(12-byte IV + ciphertext + authTag)
export async function decrypt(
  packed: string,
  key: CryptoKey
): Promise<string> {
  const raw = Uint8Array.from(atob(packed), (c) => c.charCodeAt(0));

  const iv = raw.slice(0, 12);
  const ciphertext = raw.slice(12); // WebCrypto expects authTag appended to ciphertext

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  return new TextDecoder().decode(plaintext);
}

// ── Passphrase verification ──
// Known plaintext that gets encrypted when a passphrase is first set.
// On subsequent unlocks, we decrypt this to verify the passphrase is correct.
export const VERIFICATION_PLAINTEXT = 'lifespace-vault-verify';

// ── Ephemeral share link crypto ──
// Uses a SEPARATE random key (not the user's vault key)
// The key lives in the URL fragment (#), never sent to server

export async function generateShareKey(): Promise<{
  key: CryptoKey;
  keyHex: string;
}> {
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM' },
    true, // extractable — we need to put it in the URL
    ['encrypt', 'decrypt']
  );
  return { key, keyHex: bytesToHex(rawKey) };
}

export async function importShareKey(keyHex: string): Promise<CryptoKey> {
  const rawKey = hexToBytes(keyHex);
  return crypto.subtle.importKey(
    'raw',
    rawKey.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
}

// ── Clipboard auto-clear ──
export function copyToClipboard(text: string, clearAfterMs = 30000): void {
  navigator.clipboard.writeText(text);

  // Auto-clear after 30 seconds (per vault concept spec)
  if (clearAfterMs > 0) {
    setTimeout(() => {
      navigator.clipboard.writeText('').catch(() => {
        // Clipboard API may fail if tab lost focus — acceptable
      });
    }, clearAfterMs);
  }
}
